import { Box, Button, Flex, Icon } from '@chakra-ui/react';
import { useState } from 'react';

import { QuestionCard } from './QuestionCard';
import type { ClarifyingQuestion, QuestionAnswer } from './types';

// Arrow icons
const ArrowLeftIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 19.5L8.25 12l7.5-7.5"
    />
  </svg>
);

const ArrowRightIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 4.5l7.5 7.5-7.5 7.5"
    />
  </svg>
);

interface QuestionsPanelProps {
  questions: ClarifyingQuestion[];
  onComplete: (answers: QuestionAnswer[]) => void;
  onSkip: () => void;
}

export function QuestionsPanel({
  questions,
  onComplete,
  onSkip: _onSkip,
}: QuestionsPanelProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, QuestionAnswer>>(
    new Map()
  );

  const currentQuestion = questions[currentIndex];
  if (!currentQuestion) {
    // Should never happen, but handle gracefully
    return null;
  }

  const currentAnswer = answers.get(currentQuestion.id) || null;

  const handleAnswerChange = (answer: QuestionAnswer) => {
    const newAnswers = new Map(answers);
    newAnswers.set(answer.questionId, answer);
    setAnswers(newAnswers);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleContinue = () => {
    // Collect all answers, using best_judgement for unanswered questions
    const allAnswers: QuestionAnswer[] = questions.map((q) => {
      const answer = answers.get(q.id);
      if (answer) {
        return answer;
      }
      // If no answer, use best_judgement (user wants AI to decide)
      return { questionId: q.id, selectedOptionIds: ['best_judgement'] };
    });
    onComplete(allAnswers);
  };

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={3}
      display="flex"
      flexDirection="column"
    >
      <QuestionCard
        question={currentQuestion}
        answer={currentAnswer}
        onAnswerChange={handleAnswerChange}
        questionIndex={currentIndex}
        totalQuestions={questions.length}
      />

      <Flex justify="flex-end" align="center" gap={2} mt={3} flexShrink={0}>
        {questions.length > 1 && (
          <Flex gap={2} mr="auto">
            <Button
              size="xs"
              variant="outline"
              onClick={handlePrevious}
              isDisabled={currentIndex === 0}
              leftIcon={<Icon as={ArrowLeftIcon} boxSize={3} />}
              borderColor="gray.300"
            >
              Previous
            </Button>
            {currentIndex < questions.length - 1 && (
              <Button
                size="xs"
                variant="outline"
                onClick={handleNext}
                rightIcon={<Icon as={ArrowRightIcon} boxSize={3} />}
                borderColor="gray.300"
              >
                Next
              </Button>
            )}
          </Flex>
        )}

        <Button
          size="xs"
          colorScheme="green"
          onClick={handleContinue}
          rightIcon={<Icon as={ArrowRightIcon} boxSize={3} />}
        >
          Continue
        </Button>
      </Flex>
    </Box>
  );
}
